// The interface, drawn on the same page as the game: cards torn from the notebook, a pen-filled
// health bar, a quest note held by a paperclip, and a satchel with real item slots.
import {
  inkLine, inkPath, inkEllipse, hatch, penText, measureText, FONT_PEN, FONT_HAND,
  INK, INK_SOFT, PENCIL, PENCIL_SOFT, RED, GREEN, GOLD, MARGIN, RULE, W, hash,
} from './ink.ts';
import { ITEMS, QUESTS, CLASSES } from '../../../packages/game-data/src/index.ts';
import type { CharacterState, EntityState, ZoneInfo } from '../../../packages/protocol/src/index.ts';

// ---------- building blocks ----------
/** a card torn from the page: soft shadow, paper fill, a hand-drawn border */
export function card(
  ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number,
  o: { seed?: number; tilt?: number; color?: string; margin?: boolean; torn?: boolean; tape?: boolean; ruled?: boolean } = {},
) {
  const seed = o.seed ?? 3, tilt = o.tilt ?? 0;
  const torn = o.torn ?? true, tape = o.tape ?? true, ruled = o.ruled ?? true;
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2); ctx.rotate(tilt); ctx.translate(-(x + w / 2), -(y + h / 2));

  // A scrap torn out of the same notebook: the left edge is ragged, the rest was cut along a rule.
  const pts: [number, number][] = [];
  const rag = (t: number, i: number) => (torn ? (hash(seed + 91, i) - 0.5) * 5.5 : 0);
  const steps = Math.max(4, Math.round(h / 12));
  pts.push([x, y]);
  for (let i = 1; i < steps; i++) pts.push([x + rag(i / steps, i), y + (h * i) / steps]);
  pts.push([x, y + h], [x + w, y + h], [x + w, y]);

  const trace = (dx: number, dy: number) => {
    ctx.beginPath();
    pts.forEach(([px, py], i) => (i ? ctx.lineTo(px + dx, py + dy) : ctx.moveTo(px + dx, py + dy)));
    ctx.closePath();
  };
  // a flat offset shadow: canvas shadowBlur costs far too much per frame on a phone
  ctx.save();
  ctx.fillStyle = 'rgba(40,36,24,0.16)'; trace(3, 4); ctx.fill();
  ctx.fillStyle = 'rgba(253,251,244,0.97)'; trace(0, 0); ctx.fill();
  if (ruled) {
    ctx.save(); trace(0, 0); ctx.clip();
    ctx.strokeStyle = RULE; ctx.lineWidth = 1; ctx.globalAlpha = 0.5;
    ctx.beginPath();
    for (let ry = y + 16; ry < y + h - 3; ry += 17) { ctx.moveTo(x + 1, ry); ctx.lineTo(x + w - 1, ry); }
    ctx.stroke();
    ctx.restore();
  }
  ctx.restore();

  inkPath(ctx, pts, { color: o.color ?? INK, width: W.edge, seed, amp: 0.9, close: true });
  if (o.margin) inkLine(ctx, x + 14, y + 3, x + 14, y + h - 3, { color: MARGIN, width: W.detail, amp: 0.8, alpha: 0.6, seed: seed + 1 });
  if (tape) { tapeStrip(ctx, x + 12, y - 4, seed); tapeStrip(ctx, x + w - 34, y + h - 8, seed + 5); }
  ctx.restore();
  return { x, y, w, h };
}

/** a strip of tape holding a scrap to the page: cloudy, with a torn end */
function tapeStrip(ctx: CanvasRenderingContext2D, x: number, y: number, seed: number) {
  const w = 30, h = 13;
  ctx.save();
  ctx.translate(x + w / 2, y + h / 2); ctx.rotate((hash(seed, 3) - 0.5) * 0.5); ctx.translate(-(x + w / 2), -(y + h / 2));
  ctx.globalAlpha = 0.34; ctx.fillStyle = '#cfd4c8';
  ctx.beginPath();
  ctx.moveTo(x, y + 1.5);
  ctx.lineTo(x + w * 0.5, y);
  ctx.lineTo(x + w, y + 2);
  ctx.lineTo(x + w - 2, y + h);
  ctx.lineTo(x + w * 0.45, y + h - 2);
  ctx.lineTo(x + 1, y + h - 0.5);
  ctx.closePath(); ctx.fill();
  ctx.globalAlpha = 0.5; ctx.strokeStyle = '#a9b0a2'; ctx.lineWidth = 0.7; ctx.stroke();
  ctx.globalAlpha = 0.25; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(x + 3, y + 4); ctx.lineTo(x + w - 5, y + 3.4); ctx.stroke();
  ctx.restore();
}

/** a bar the way you would draw one: an outline, filled with quick strokes */
export function inkBar(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, k: number, color: string, seed: number) {
  inkPath(ctx, [[x, y], [x + w, y], [x + w, y + h], [x, y + h]], { color: PENCIL, width: W.detail, seed, amp: 0.7, close: true });
  const fw = Math.max(0, Math.min(1, k)) * (w - 3);
  if (fw > 1) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x + 1.5, y + 1.5, fw, h - 3); ctx.clip();
    ctx.fillStyle = color; ctx.globalAlpha = 0.9; ctx.fillRect(x + 1.5, y + 1.5, fw, h - 3);
    // a few diagonal strokes on top keep it looking filled in by hand, without twenty of them
    ctx.globalAlpha = 0.35; ctx.strokeStyle = '#ffffff'; ctx.lineWidth = 1.2;
    ctx.beginPath();
    for (let i = -h; i < fw + h; i += 7) { ctx.moveTo(x + i, y + h - 1); ctx.lineTo(x + i + h, y + 1); }
    ctx.stroke();
    ctx.restore();
  }
}

function paperclip(ctx: CanvasRenderingContext2D, x: number, y: number, seed: number) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(-0.25);
  inkPath(ctx, [[0, 0], [0, 15], [5, 18], [9, 15], [9, 3], [4.5, 0], [2, 3], [2, 13]], { color: PENCIL_SOFT, width: 1.6, seed, amp: 0.5 });
  ctx.restore();
}

// ---------- item icons ----------
export function itemIcon(ctx: CanvasRenderingContext2D, id: string, x: number, y: number, s: number, seed: number) {
  const def = ITEMS[id]; if (!def) return;
  const look = def.look;
  const c = look?.color ?? PENCIL, ac = look?.accent ?? GOLD;
  ctx.save(); ctx.translate(x, y); ctx.scale(s / 16, s / 16);
  switch (def.cat) {
    case 'weapon': {
      if (look?.kind === 'bow') { inkPath(ctx, [[-4, -8], [4, -3], [5, 0], [4, 3], [-4, 8]], { color: c, width: 1.8, seed, amp: 0.6 }); inkLine(ctx, -4, -8, -4, 8, { color: ac, width: 1, amp: 0.4, seed: seed + 1 }); break; }
      if (look?.kind === 'staff') { inkLine(ctx, -2, 8, 2, -6, { color: c, width: 2.2, amp: 0.5, seed }); inkPath(ctx, [[0, -6], [4, -6], [3, -10], [1, -10]], { color: ac, width: 1.4, seed: seed + 1, amp: 0.5, close: true }); break; }
      if (look?.kind === 'axe') { inkLine(ctx, -1, 8, 1, -8, { color: '#4a3a24', width: 2.2, amp: 0.5, seed }); inkPath(ctx, [[1, -8], [8, -5], [8, 0], [1, -2]], { color: c, width: 1.6, seed: seed + 1, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.5)' }); break; }
      inkLine(ctx, 0, 8, 0, 3, { color: '#4a3a24', width: 2.4, amp: 0.4, seed });
      inkLine(ctx, -4, 3, 4, 3, { color: ac, width: 1.8, amp: 0.4, seed: seed + 1 });
      inkPath(ctx, [[-1.8, 3], [1.8, 3], [1.4, -7], [0, -10], [-1.4, -7]], { color: c, width: 1.5, seed: seed + 2, amp: 0.5, close: true, fill: 'rgba(255,255,255,0.6)' });
      break;
    }
    case 'offhand': {
      if (look?.shape === 'round') inkEllipse(ctx, 0, 0, 7.5, 8, { color: c, width: 1.8, seed, amp: 0.7, fill: 'rgba(255,255,255,0.55)' });
      else if (look?.shape === 'kite') inkPath(ctx, [[-6, -7], [6, -7], [5, 2], [0, 9], [-5, 2]], { color: c, width: 1.8, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.55)' });
      else inkPath(ctx, [[-6, -8], [6, -8], [6, 8], [-6, 8]], { color: c, width: 1.8, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.6)' });
      inkLine(ctx, -3, 0, 3, 0, { color: ac, width: 1.1, amp: 0.4, seed: seed + 1 });
      break;
    }
    case 'head': {
      if (look?.kind === 'crown') { inkPath(ctx, [[-7, 4], [-7, -2], [-3.5, 1], [0, -5], [3.5, 1], [7, -2], [7, 4]], { color: c, width: 1.7, seed, amp: 0.5, close: true, fill: 'rgba(201,144,36,0.3)' }); break; }
      if (look?.kind === 'cap') { inkPath(ctx, [[-8, 3], [0, -7], [8, 3]], { color: c, width: 1.8, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.6)' }); break; }
      inkPath(ctx, [[-7, 5], [-7, -1], [0, -8], [7, -1], [7, 5]], { color: c, width: 1.8, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.55)' });
      inkLine(ctx, 0, -1, 0, 6, { color: c, width: 1.4, amp: 0.4, seed: seed + 1 });
      break;
    }
    case 'chest': {
      inkPath(ctx, [[-6, -7], [6, -7], [7, 0], [5, 8], [-5, 8], [-7, 0]], { color: c, width: 1.8, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.5)' });
      inkLine(ctx, 0, -6, 0, 7, { color: ac, width: 1, amp: 0.4, alpha: 0.8, seed: seed + 1 });
      break;
    }
    case 'cape': {
      inkPath(ctx, [[-6, -7], [6, -7], [8, 8], [-8, 8]], { color: c, width: 1.7, seed, amp: 0.7, close: true, fill: 'rgba(194,42,42,0.2)' });
      break;
    }
    case 'boots': {
      inkPath(ctx, [[-5, -6], [0, -6], [1, 3], [6, 5], [6, 8], [-5, 8]], { color: c, width: 1.7, seed, amp: 0.6, close: true, fill: 'rgba(255,255,255,0.5)' });
      break;
    }
    case 'consumable': {
      inkPath(ctx, [[-3, -7], [3, -7], [3, -3], [5, 2], [5, 8], [-5, 8], [-5, 2], [-3, -3]], { color: INK_SOFT, width: 1.6, seed, amp: 0.5, close: true, fill: 'rgba(61,85,160,0.25)' });
      inkLine(ctx, -4, 3, 4, 3, { color: INK, width: 1, amp: 0.5, alpha: 0.7, seed: seed + 1 });
      break;
    }
    default: {
      inkPath(ctx, [[-6, -5], [5, -7], [7, 4], [-4, 7]], { color: PENCIL, width: 1.5, seed, amp: 0.8, close: true, fill: 'rgba(255,255,255,0.5)' });
      inkLine(ctx, -4, -2, 4, -3, { color: PENCIL_SOFT, width: 0.9, amp: 0.5, alpha: 0.7, seed: seed + 1 });
    }
  }
  ctx.restore();
}

// ---------- the status card ----------
export function drawStatus(ctx: CanvasRenderingContext2D, c: CharacterState, me: EntityState | undefined, x: number, y: number, scale = 1) {
  const w = 208 * scale, h = 104 * scale;
  card(ctx, x, y, w, h, { seed: 11, tilt: -0.012, color: INK });
  const hp = Math.round(me?.hp ?? c.hp), hpMax = me?.hpMax ?? c.hpMax;
  penText(ctx, c.name, x + 12, y + 27 * scale, { size: 25 * scale, color: INK, font: FONT_PEN, seed: 12 });
  let atk = CLASSES[c.cls].atk + Math.round(c.level * 1.5), armor = 0;
  for (const id of Object.values(c.equip)) { const it = id ? ITEMS[id] : undefined; if (!it) continue; atk += it.atk ?? 0; armor += it.armor ?? 0; }
  penText(ctx, `${CLASSES[c.cls].name} · level ${c.level}`, x + 13, y + 41 * scale, { size: 13 * scale, color: PENCIL, seed: 13 });
  penText(ctx, `attack ${atk}`, x + 13, y + 55 * scale, { size: 12.5 * scale, color: RED, seed: 131 });
  penText(ctx, `armour ${armor}`, x + 74 * scale, y + 55 * scale, { size: 12.5 * scale, color: INK_SOFT, seed: 132 });
  inkBar(ctx, x + 12, y + 61 * scale, w - 74 * scale, 11 * scale, hp / Math.max(1, hpMax), RED, 14);
  penText(ctx, `${hp}/${hpMax}`, x + w - 56 * scale, y + 71 * scale, { size: 13 * scale, color: PENCIL, seed: 15 });
  inkBar(ctx, x + 12, y + 77 * scale, w - 74 * scale, 7 * scale, c.xp / Math.max(1, c.xpNext), GREEN, 16);
  penText(ctx, 'xp', x + w - 56 * scale, y + 85 * scale, { size: 12 * scale, color: PENCIL_SOFT, seed: 17 });
  // coins
  inkEllipse(ctx, x + 18, y + 94 * scale, 5.5 * scale, 5.5 * scale, { color: GOLD, width: 1.5, seed: 18, amp: 0.5, fill: 'rgba(201,144,36,0.25)' });
  penText(ctx, `${c.coins}`, x + 28, y + 98 * scale, { size: 14 * scale, color: PENCIL, seed: 19 });
}

// ---------- quest note ----------
/** how tall the quest note will be, so panels under it can be placed without guessing */
export function questNoteHeight(c: CharacterState): number {
  const st = c.quests.find(q => !q.done); if (!st) return 0;
  const def = QUESTS[st.id]; if (!def) return 0;
  const step = def.steps[st.step]; if (!step) return 0;
  return 54 + Math.max(1, Math.ceil(step.text.length / 34)) * 17 + (step.kill ? 16 : 0);
}

export function drawQuestNote(ctx: CanvasRenderingContext2D, c: CharacterState, x: number, y: number, w: number) {
  const st = c.quests.find(q => !q.done);
  if (!st) return;
  const def = QUESTS[st.id]; if (!def) return;
  const step = def.steps[st.step]; if (!step) return;
  const lines = wrap(ctx, step.text, w - 28, 14);
  const h = 54 + lines.length * 17 + (step.kill ? 16 : 0);
  card(ctx, x, y, w, h, { seed: 21, tilt: 0.014, color: MARGIN, margin: true });
  paperclip(ctx, x + w - 22, y - 8, 22);
  penText(ctx, def.title, x + 22, y + 26, { size: 21, color: MARGIN, font: FONT_PEN, seed: 23 });
  lines.forEach((ln, i) => penText(ctx, ln, x + 22, y + 46 + i * 17, { size: 14, color: INK, seed: 24 + i }));
  if (step.kill) {
    const have = st.counters[step.kill.def] ?? 0;
    const done = have >= step.kill.count;
    penText(ctx, `${have} / ${step.kill.count}`, x + 22, y + 52 + lines.length * 17, { size: 15, color: done ? GREEN : RED, seed: 30 });
    if (done) inkLine(ctx, x + 20, y + 47 + lines.length * 17, x + 64, y + 47 + lines.length * 17, { color: GREEN, width: 1.6, amp: 1, seed: 31 });
  }
}

function wrap(ctx: CanvasRenderingContext2D, text: string, maxW: number, size: number) {
  ctx.save(); ctx.font = `${size}px ${FONT_HAND}`;
  const out: string[] = []; let line = '';
  for (const word of text.split(' ')) {
    const test = line ? line + ' ' + word : word;
    if (ctx.measureText(test).width > maxW && line) { out.push(line); line = word; } else line = test;
  }
  if (line) out.push(line);
  ctx.restore();
  return out;
}

// ---------- target plate ----------
export function drawTargetPlate(ctx: CanvasRenderingContext2D, e: EntityState, cx: number, y: number) {
  const name = e.name ?? '';
  const w = Math.max(150, measureText(ctx, name, 16) + 56);
  card(ctx, cx - w / 2, y, w, 40, { seed: 41, tilt: 0.008, color: PENCIL });
  penText(ctx, name, cx - w / 2 + 12, y + 19, { size: 16, color: INK, seed: 42 });
  inkBar(ctx, cx - w / 2 + 12, y + 24, w - 24, 8, (e.hp ?? 0) / Math.max(1, e.hpMax ?? 1), RED, 43);
}

// ---------- satchel ----------
export interface SatchelHit { slot: number; x: number; y: number; w: number; h: number }
export function drawSatchel(ctx: CanvasRenderingContext2D, c: CharacterState, x: number, y: number, w: number, h: number, compact = false, hover?: { x: number; y: number }): SatchelHit[] {
  card(ctx, x, y, w, h, { seed: 51, tilt: -0.006, color: INK });
  penText(ctx, 'Satchel', x + 16, y + 30, { size: 24, color: INK, font: FONT_PEN, seed: 52 });
  penText(ctx, 'click an item, or press 1-9', x + 16, y + 47, { size: 12, color: PENCIL_SOFT, seed: 53 });
  inkLine(ctx, x + 12, y + 54, x + w - 12, y + 54, { color: PENCIL_SOFT, width: 1, amp: 1, alpha: 0.5, seed: 54 });

  const cols = compact ? 4 : 3;
  const gap = 8;
  const gridW = compact ? w - 32 : w * 0.5;
  const cell = Math.min(46, Math.floor((gridW - gap * (cols - 1)) / cols));
  const gx = x + 16, gy = y + 64;
  const hits: SatchelHit[] = [];
  const rows = compact ? 2 : 3;
  for (let i = 0; i < cols * rows && i < 10; i++) {
    const cxp = gx + (i % cols) * (cell + gap), cyp = gy + Math.floor(i / cols) * (cell + gap);
    const st = c.inventory[i];
    inkPath(ctx, [[cxp, cyp], [cxp + cell, cyp], [cxp + cell, cyp + cell], [cxp, cyp + cell]],
      { color: st ? PENCIL : PENCIL_SOFT, width: st ? W.detail : W.shade, seed: 60 + i, amp: 0.8, close: true, alpha: st ? 1 : 0.5 });
    penText(ctx, String(i + 1), cxp + 3, cyp + 12, { size: 11, color: PENCIL_SOFT, alpha: 0.8, seed: 70 + i });
    if (!st) continue;
    itemIcon(ctx, st.item, cxp + cell / 2, cyp + cell / 2 + 2, 26, 80 + i);
    if (st.qty > 1) penText(ctx, `x${st.qty}`, cxp + cell - 4, cyp + cell - 4, { size: 12, align: 'right', color: INK, seed: 90 + i });
    hits.push({ slot: st.slot, x: cxp, y: cyp, w: cell, h: cell });
    // the slot under the pointer is circled in red, with its name and what it gives you
    if (hover && hover.x >= cxp && hover.x <= cxp + cell && hover.y >= cyp && hover.y <= cyp + cell) {
      inkPath(ctx, [[cxp - 2, cyp - 2], [cxp + cell + 2, cyp - 2], [cxp + cell + 2, cyp + cell + 2], [cxp - 2, cyp + cell + 2]],
        { color: RED, width: 1.8, seed: 200 + i, amp: 1.1, close: true });
      const def = ITEMS[st.item];
      if (def) {
        const bits = [def.atk ? `+${def.atk} attack` : '', def.armor ? `+${def.armor} armour` : '', def.hp ? `+${def.hp} life` : '', def.speed ? `${def.speed > 0 ? '+' : ''}${def.speed} speed` : ''].filter(Boolean).join(', ');
        penText(ctx, def.name, x + 16, y + h - 26, { size: 15, color: INK, seed: 210 });
        penText(ctx, bits || def.desc, x + 16, y + h - 10, { size: 12, color: PENCIL_SOFT, seed: 211 });
      }
    }
  }

  // worn gear: down the right on a desktop, in two columns underneath on a phone
  const slots: [keyof CharacterState['equip'], string][] = [['weapon', 'hand'], ['offhand', 'off'], ['head', 'head'], ['chest', 'body'], ['cape', 'back'], ['boots', 'feet']];
  const ex0 = compact ? x + 16 : gx + cols * (cell + gap) + 10;
  const ey0 = compact ? gy + rows * (cell + gap) + 22 : y + 98;
  penText(ctx, 'worn', ex0, ey0 - 20, { size: 14, color: PENCIL, seed: 100 });
  slots.forEach(([key, label], i) => {
    const id = c.equip[key];
    const col = compact ? i % 2 : 0;
    const row = compact ? Math.floor(i / 2) : i;
    const ex = ex0 + col * ((w - 40) / 2);
    const ly = ey0 + row * 26;
    inkPath(ctx, [[ex, ly - 14], [ex + 22, ly - 14], [ex + 22, ly + 8], [ex, ly + 8]], { color: id ? PENCIL : PENCIL_SOFT, width: id ? W.detail : W.shade, seed: 110 + i, amp: 0.7, close: true, alpha: id ? 1 : 0.45 });
    if (id) itemIcon(ctx, id, ex + 11, ly - 3, 18, 120 + i);
    penText(ctx, label, ex + 27, ly + 2, { size: 12, color: id ? INK : PENCIL_SOFT, alpha: id ? 1 : 0.7, seed: 130 + i });
    if (id) {
      const nm = ITEMS[id]?.name ?? id;
      const room = compact ? (w - 40) / 2 - 66 : w * 0.5 - 76;
      let cut = nm;
      while (cut.length > 4 && measureText(ctx, cut, 12) > room) cut = cut.slice(0, -1);
      penText(ctx, cut === nm ? nm : cut.trimEnd() + '…', ex + 62, ly + 2, { size: 12, color: GREEN, seed: 140 + i });
    }
  });
  return hits;
}

// ---------- floating damage ----------
export interface Float { x: number; y: number; vx: number; text: string; t: number; life: number; color: string; big: boolean }
export function drawFloat(ctx: CanvasRenderingContext2D, f: Float) {
  const k = f.t / f.life;
  const alpha = k > 0.75 ? (1 - k) * 4 : Math.min(1, k * 3);
  const size = f.big ? 27 : 19;
  if (f.big) inkEllipse(ctx, f.x, f.y - size * 0.3, size * 0.9, size * 0.62, { color: f.color, width: 1.4, amp: 1.4, seed: 151, alpha: alpha * 0.55 });
  penText(ctx, f.text, f.x, f.y, { size, align: 'center', color: f.color, alpha, font: FONT_PEN, seed: 152 });
}

// ---------- chat ----------
export function drawChat(ctx: CanvasRenderingContext2D, lines: { from: string; ch: string; text: string; t: number }[], now: number, x: number, bottom: number, maxW: number) {
  const show = lines.slice(-6);
  show.forEach((m, i) => {
    const age = now - m.t;
    const alpha = age > 24 ? Math.max(0, 1 - (age - 24) / 6) : 1;
    if (alpha <= 0.02) return;
    const y = bottom - (show.length - 1 - i) * 20;
    let text = m.ch === 'system' ? m.text : `${m.from}: ${m.text}`;
    if (measureText(ctx, text, 15) > maxW - 14) {
      while (text.length > 6 && measureText(ctx, text, 15) > maxW - 22) text = text.slice(0, -1);
      text = text.trimEnd() + '…';
    }
    const tw = Math.min(maxW, measureText(ctx, text, 15) + 14);
    ctx.save(); ctx.globalAlpha = alpha * 0.55; ctx.fillStyle = 'rgba(253,251,244,0.9)';
    ctx.fillRect(x - 6, y - 14, tw, 19); ctx.restore();
    penText(ctx, text, x, y, { size: 15, color: m.ch === 'system' ? PENCIL_SOFT : m.ch === 'world' ? INK_SOFT : INK, alpha, seed: 160 + i });
  });
}

export { hatch };

/**
 * The page's own map, drawn on a torn scrap: the zone in miniature, with the landmarks marked the
 * way somebody sketches directions for a friend.
 */
export function drawMiniMap(
  ctx: CanvasRenderingContext2D, zone: ZoneInfo, me: { x: number; y: number } | undefined,
  npcs: { x: number; y: number }[], x: number, y: number, w: number,
) {
  const h = Math.round((w * zone.h) / zone.w) + 30;
  card(ctx, x, y, w, h, { seed: 811, tilt: -0.012 });
  const px = x + 10, py = y + 24, pw = w - 20, ph = h - 34;
  const sx = pw / zone.w, sy = ph / zone.h;
  penText(ctx, 'the page so far', x + w / 2, y + 17, { size: 14, align: 'center', color: PENCIL, seed: 812 });
  ctx.save();
  ctx.beginPath(); ctx.rect(px, py, pw, ph); ctx.clip();
  for (const p of zone.props) {
    const mx = px + (p.x + p.w / 2) * sx, my = py + (p.y + p.h / 2) * sy;
    if (p.kind === 'water') { ctx.globalAlpha = 0.5; ctx.fillStyle = '#8fb6dd'; ctx.fillRect(px + p.x * sx, py + p.y * sy, p.w * sx, p.h * sy); ctx.globalAlpha = 1; continue; }
    if (p.kind === 'tree' || p.kind === 'bush') {
      inkPath(ctx, [[mx - 2.6, my + 2], [mx, my - 3.4], [mx + 2.6, my + 2]], { color: GREEN, width: 0.8, amp: 0.3, close: true, alpha: 0.75, seed: p.seed });
    } else if (p.kind === 'hut') {
      inkPath(ctx, [[mx - 3, my + 2.4], [mx - 3, my - 0.6], [mx, my - 3.4], [mx + 3, my - 0.6], [mx + 3, my + 2.4]], { color: RED, width: 0.9, amp: 0.3, close: true, alpha: 0.85, seed: p.seed });
    } else if (p.kind === 'rock') {
      inkPath(ctx, [[mx - 2.4, my + 1.6], [mx - 1, my - 2], [mx + 2.2, my - 0.6], [mx + 1.6, my + 1.8]], { color: PENCIL_SOFT, width: 0.7, amp: 0.25, close: true, alpha: 0.7, seed: p.seed });
    }
  }
  for (const n of npcs) {
    ctx.fillStyle = INK; ctx.globalAlpha = 0.8;
    ctx.beginPath(); ctx.arc(px + n.x * sx, py + n.y * sy, 2, 0, 7); ctx.fill(); ctx.globalAlpha = 1;
  }
  if (me) {
    const mx = px + me.x * sx, my = py + me.y * sy;
    inkLine(ctx, mx - 4, my - 4, mx + 4, my + 4, { color: RED, width: 1.6, amp: 0.5, seed: 813 });
    inkLine(ctx, mx + 4, my - 4, mx - 4, my + 4, { color: RED, width: 1.6, amp: 0.5, seed: 814 });
    inkEllipse(ctx, mx, my, 7, 7, { color: RED, width: 0.8, amp: 0.6, alpha: 0.5, seed: 815 });
  }
  ctx.restore();
  inkPath(ctx, [[px, py], [px + pw, py], [px + pw, py + ph], [px, py + ph]], { color: PENCIL_SOFT, width: 0.8, amp: 0.6, close: true, alpha: 0.6, seed: 816 });
  penText(ctx, 'you', x + w - 14, y + h - 6, { size: 12, align: 'right', color: RED, seed: 817 });
}

// ---------- the character sheet ----------
// Your hero, drawn several times the size he is in play, with what he is wearing laid out round
// him. Gameplay has to keep the figure small enough to read at a glance; this is where the armour
// you worked for is actually worth looking at.
export interface SlotHit { slot: string; x: number; y: number; w: number; h: number }
const SLOTS: { key: string; label: string }[] = [
  { key: 'head', label: 'head' },
  { key: 'chest', label: 'body' },
  { key: 'cape', label: 'back' },
  { key: 'weapon', label: 'hand' },
  { key: 'offhand', label: 'off' },
  { key: 'boots', label: 'feet' },
];

export function drawCharacterSheet(
  ctx: CanvasRenderingContext2D, c: CharacterState,
  x: number, y: number, w: number, h: number,
  drawFigure: (ctx: CanvasRenderingContext2D, eq: Record<string, string | undefined>) => void,
  hover?: { x: number; y: number },
): SlotHit[] {
  card(ctx, x, y, w, h, { seed: 141, tilt: -0.004, color: INK });
  penText(ctx, c.name, x + 20, y + 34, { size: 26, color: INK, font: FONT_PEN, seed: 142 });
  penText(ctx, `${CLASSES[c.cls]?.name ?? c.cls} · level ${c.level}`, x + 20, y + 52, { size: 13, color: PENCIL, seed: 143 });
  inkLine(ctx, x + 14, y + 60, x + w - 14, y + 60, { color: PENCIL_SOFT, width: 1, amp: 1, alpha: 0.5, seed: 144 });

  // the figure, as big as the sheet allows
  const stageW = Math.min(w * 0.46, 240);
  const stageX = x + 16, stageY = y + 70, stageH = h - 140;
  const scale = Math.min(stageW / 130, stageH / 96);
  ctx.save();
  ctx.translate(stageX + stageW / 2, stageY + stageH * 0.94);
  ctx.scale(scale, scale);
  drawFigure(ctx, c.equip as Record<string, string | undefined>);
  ctx.restore();
  // a line under his feet so he is standing on something
  inkLine(ctx, stageX + 20, stageY + stageH * 0.95, stageX + stageW - 20, stageY + stageH * 0.95,
    { color: PENCIL_SOFT, width: 1.4, amp: 1.4, alpha: 0.55, seed: 145 });

  // the slots, down the right
  const hits: SlotHit[] = [];
  const colX = x + stageW + 40;
  const cell = Math.min(52, Math.floor((h - 150) / SLOTS.length) - 6);
  SLOTS.forEach((s, i) => {
    const sy = y + 74 + i * (cell + 6);
    const id = (c.equip as Record<string, string | undefined>)[s.key];
    const def = id ? ITEMS[id] : undefined;
    const over = hover && hover.x >= colX && hover.x <= colX + cell && hover.y >= sy && hover.y <= sy + cell;
    inkPath(ctx, [[colX, sy], [colX + cell, sy], [colX + cell, sy + cell], [colX, sy + cell]],
      { color: def ? PENCIL : PENCIL_SOFT, width: def ? W.detail : W.shade, seed: 150 + i, amp: 0.8, close: true, alpha: def ? 1 : 0.5 });
    if (over) inkPath(ctx, [[colX - 2, sy - 2], [colX + cell + 2, sy - 2], [colX + cell + 2, sy + cell + 2], [colX - 2, sy + cell + 2]],
      { color: RED, width: 1.4, seed: 160 + i, amp: 1, close: true, alpha: 0.8 });
    if (def) itemIcon(ctx, def.id, colX + cell / 2, sy + cell / 2 + 2, cell * 0.56, 170 + i);
    penText(ctx, s.label, colX + cell + 10, sy + cell * 0.45, { size: 13, color: PENCIL_SOFT, seed: 180 + i });
    penText(ctx, def?.name ?? '—', colX + cell + 10, sy + cell * 0.82,
      { size: 14, color: def ? INK : PENCIL_SOFT, alpha: def ? 1 : 0.6, seed: 190 + i });
    hits.push({ slot: s.key, x: colX, y: sy, w: cell, h: cell });
  });

  // what all of it adds up to
  let atk = (CLASSES[c.cls]?.atk ?? 0) + Math.round(c.level * 1.5), armor = 0, hp = 0, speed = 0;
  for (const id of Object.values(c.equip)) {
    const it = id ? ITEMS[id] : undefined;
    if (!it) continue;
    atk += it.atk ?? 0; armor += it.armor ?? 0; hp += it.hp ?? 0; speed += it.speed ?? 0;
  }
  const by = y + h - 44;
  inkLine(ctx, x + 14, by - 14, x + w - 14, by - 14, { color: PENCIL_SOFT, width: 1, amp: 1, alpha: 0.5, seed: 146 });
  const stats: [string, string, string][] = [
    ['attack', String(atk), RED],
    ['armour', String(armor), INK],
    ['health', String(c.hpMax), GREEN],
    ['speed', (speed >= 0 ? '+' : '') + speed, GOLD],
  ];
  const sw = (w - 40) / stats.length;
  stats.forEach(([label, val, col], i) => {
    const sx = x + 20 + sw * i;
    penText(ctx, label, sx, by + 2, { size: 12, color: PENCIL_SOFT, seed: 200 + i });
    penText(ctx, val, sx, by + 24, { size: 24, color: col, font: FONT_PEN, seed: 210 + i });
  });
  penText(ctx, 'tap a slot to take it off', x + w - 20, y + h - 8, { size: 11, align: 'right', color: PENCIL_SOFT, alpha: 0.8, seed: 147 });
  return hits;
}
